import { ref, watch, computed } from 'vue';
import { useComponentI18n } from '@rdrive/i18n';
import messages from './messages/index.js';
import { useFilterContextRefresh } from '@rdrive/data-filters';
import { chartTheme, useChartTheme } from '@rdrive/theme';
import { downloadChartAsPNG } from '@rdrive/helper-chart-export';

export default (props) => {
  // LOCAL composer with the warn flags off (see @rdrive/i18n): `others` — the only string
  // this component invents — comes from ./messages; the chart's `title`/`seriesName`
  // arrive already translated as props.
  const { t } = useComponentI18n(messages);

  const chartRef = ref(null);
  const chartOption = ref({});
  const selectedTeamId = ref(null);
  const isDrillDown = ref(false);
  // The opt-in `drill` level (issue-analysis's Created By donut): `inDrill` while the children
  // of `selectedTeamId` are shown, `selectedChildId` = the child filtered by. Without `drill`
  // both stay at their initial values and every path below is the one-level donut's.
  const inDrill = ref(false);
  const selectedChildId = ref(null);

  const threshold = 0.05; // 5% threshold for showing labels

  // Slices below the threshold collapse into a clickable "Others" slice that
  // drills into its children
  const processTeamData = (rows) => {
    const totalItems = rows.reduce((sum, row) => sum + row.value, 0);
    const mainData = [];
    const othersData = [];

    rows.forEach(row => {
      const item = {
        ...row,
        name: row.name || `Unknown Team (${row.teamId})`,
        itemStyle: { color: getRandomColor(row.teamId) }
      };

      if (item.value / totalItems >= threshold) {
        mainData.push(item);
      } else {
        othersData.push(item);
      }
    });

    if (othersData.length > 0) {
      const othersValue = othersData.reduce((sum, item) => sum + item.value, 0);
      mainData.push({
        value: othersValue,
        name: t('others'),
        itemStyle: { color: chartTheme().othersColor },
        children: othersData
      });
    }

    return mainData;
  };

  const updateChartOption = (data) => {
    chartOption.value = {
      tooltip: {
        trigger: 'item',
        confine: true,
        enterable: true,
        extraCssText: 'max-width: 320px; overflow-x: auto;',
        formatter: '{b}: {c} ({d}%)'
      },
      series: [
        {
          name: (inDrill.value && props.drill.seriesName) || props.seriesName || props.title,
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: true,
          padAngle: 2,
          itemStyle: {
            borderRadius: 10,
          },
          label: {
            show: true,
            position: 'outside',
            formatter: (params) => {
              return `${params.name}\n${params.value} (${params.percent.toFixed(1)}%)`;
            },
            textStyle: {
              color: props.chartFontColor
            }
          },
          labelLine: {
            show: true,
            length: 10,
            length2: 10,
            lineStyle: {
              color: '#bfd4e0'
            }
          },
          labelLayout: {
            hideOverlap: true
          },
          emphasis: {
            label: {
              show: true,
              fontSize: '20',
              fontWeight: 'bold'
            }
          },
          data: data
        }
      ]
    };
  };

  const handleChartClick = async (params) => {
    if (params.data) {
      if (params.data.name === t('others') && !isDrillDown.value) {
        isDrillDown.value = true;
        updateChartOption(params.data.children);
      } else {
        const teamId = params.data.teamId;
        if (inDrill.value) {
          await handleDrillClick(teamId);
        } else if (teamId) {
          // Clicking the selected slice again clears the filter
          if (selectedTeamId.value === teamId) {
            selectedTeamId.value = null;
            await props.onSelect(null);
          } else if (props.drill) {
            // Show the slice's children first, then filter by the slice
            selectedTeamId.value = teamId;
            inDrill.value = true;
            updateChartOption(await loadDrillData(teamId));
            await props.onSelect(teamId);
          } else {
            selectedTeamId.value = teamId;
            await props.onSelect(teamId);
          }
        }
        if (isDrillDown.value) {
          isDrillDown.value = false;
          // Back from the "Others" children — at the top level only; inside the drill the
          // children stay until the filter tick reloads the level
          if (!inDrill.value) {
            const data = await loadTeamData();
            updateChartOption(data);
          }
        }
      }
    }
  };

  // A click inside the opt-in drill level: a new child filters by it; the selected child
  // again, or a slice without a key, returns to the top level and clears the filter.
  const handleDrillClick = async (childId) => {
    if (childId && selectedChildId.value !== childId) {
      selectedChildId.value = childId;
      await props.drill.onSelect(childId, selectedTeamId.value);
      return;
    }
    selectedChildId.value = null;
    selectedTeamId.value = null;
    inDrill.value = false;
    updateChartOption(await loadTeamData());
    await props.onSelect(null);
  };

  // Same hash-to-index rule as before; the palette is now the shared design token
  // (`@rdrive/theme`), which is byte-identical to the array that used to live here —
  // so every keyed slice keeps the colour it had. A slice with no key ("Unassigned") is
  // the stable `unassignedColor` grey (2026-10-07; it used to be a random palette pick
  // that changed on every render).
  const getRandomColor = (id) => {
    const colors = chartTheme().palette;

    if (!id) {
      return chartTheme().unassignedColor;
    }

    const index = id.toString().split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    return colors[index];
  };

  // Query failures render an empty donut rather than leaving stale slices,
  // matching the component's previous behaviour
  const loadTeamData = async () => {
    try {
      return processTeamData(await props.dataProvider());
    } catch (error) {
      console.error("Query execution error:", error);
      return [];
    }
  };

  // The drill level's rows for one parent slice — the same processing and failure rule
  const loadDrillData = async (parentId) => {
    try {
      return processTeamData(await props.drill.dataProvider(parentId));
    } catch (error) {
      console.error("Query execution error:", error);
      return [];
    }
  };

  // Reloads the level on show: the top, or the drill level of the selected slice
  const loadChartData = async () => {
    updateChartOption(await (inDrill.value ? loadDrillData(selectedTeamId.value) : loadTeamData()));
  };

  watch(() => props.chartFontColor, () => {
    loadChartData();
  });

  // --- PNG export (opt-in `exportFilename`) --------------------------------------------
  // The export's colours follow the page's theme (the mode only); the legend is one entry per
  // slice on screen, in its colour
  const theme = useChartTheme();
  const hasData = computed(() => (chartOption.value.series?.[0]?.data?.length ?? 0) > 0);
  const downloadPng = () => {
    const chart = chartRef.value?.chart;
    if (!chart || !props.exportFilename || !hasData.value) return;
    downloadChartAsPNG(chart, props.exportFilename, null, {
      mode: theme.value.mode,
      legend: 'items',
      title: props.exportTitle || props.title || props.seriesName,
    });
  };

  // Initial load (now gated on dataLoaded, which this chart previously lacked),
  // plus a reload on every filterContext change.
  useFilterContextRefresh(
    computed(() => props.filterContext),
    computed(() => props.dataLoaded),
    loadChartData,
    { debounce: 0, immediate: true, requireDataLoaded: true }
  );

  return {
    // `chartRef` is a STRING template ref (`ref="chartRef"` in component.vue) — it
    // only binds because it is returned under that exact name. `t` stays unreturned:
    // this template never calls it, only the "Others" slice label does.
    chartRef,
    chartOption,
    handleChartClick,
    hasData,
    downloadPng,
  };
};
